import {
  VoteStats,
  VoteLeaderboardEntry,
  VoteUserBreakdown,
  VoteServiceBreakdown,
  VoteServiceInfo,
  VoteHistoryResponse,
  VotePeriod,
} from '../types/vote';

const VOTES_API_BASE_URL = 'https://spy.playcdu.co/api/notes/votes';

class VotesApiService {
  private async request<T>(endpoint: string, params?: Record<string, string>): Promise<T> {
    const queryString = params ? '?' + new URLSearchParams(params).toString() : '';
    const response = await fetch(`${VOTES_API_BASE_URL}${endpoint}${queryString}`, {
      // Cloudflare Access handles authentication via cookies
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  async getStats(period: VotePeriod = 'month'): Promise<VoteStats> {
    return this.request<VoteStats>('/stats', { period });
  }

  async getLeaderboard(period: VotePeriod = 'month', limit?: number): Promise<VoteLeaderboardEntry[]> {
    const params: Record<string, string> = { period };
    if (limit) params.limit = limit.toString();
    return this.request<VoteLeaderboardEntry[]>('/leaderboard', params);
  }

  async getByUser(username: string, period: VotePeriod = 'month'): Promise<VoteUserBreakdown> {
    return this.request<VoteUserBreakdown>(`/by-user/${encodeURIComponent(username)}`, { period });
  }

  async getByService(serviceName: string, period: VotePeriod = 'month'): Promise<VoteServiceBreakdown> {
    return this.request<VoteServiceBreakdown>(`/by-service/${encodeURIComponent(serviceName)}`, { period });
  }

  async getServices(period: VotePeriod = 'month'): Promise<VoteServiceInfo[]> {
    return this.request<VoteServiceInfo[]>('/services', { period });
  }

  async getHistory(period: VotePeriod = 'month', granularity: string = 'day', username?: string): Promise<VoteHistoryResponse> {
    const params: Record<string, string> = { period, granularity };
    if (username) params.username = username;
    return this.request<VoteHistoryResponse>('/history', params);
  }
}

export const votesApiService = new VotesApiService();
